import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  Admin,
  Container,
  ContainerContent,
  ContainerHeader,
} from '@/component';
import { ChangePlan } from '@/component/Admin';
import {
  useAllObjectClassListQuery,
  useTenantUsageQuery,
  useUserCountQuery,
} from '@/hook';
import {
  CheckCircle,
  Cloud,
  DirectionsCar,
  Groups,
  Schedule,
  SmartToy,
  VDialog,
  VFlex,
  VStack,
  VTab,
  VTabTrigger,
  VTabTriggerContainer,
  VTextButton,
  useUnleash,
} from '@/package';
import { useMainLayout } from '@/provider';
import { useAuthStore, useTenantStore } from '@/store';
import { bytesToGB, minutesToH, minutesToHM } from '@/util';

import { OpenObjectClassManagementButton } from './OpenObjectClassManagementButton';

const AdminSettingPage = () => {
  const { getCurrentTenant } = useTenantStore();
  const currentTenant = getCurrentTenant();
  const { data: workerCount } = useUserCountQuery({ role: 'Worker' });
  const { data: managerCount } = useUserCountQuery({ role: 'Manager' });
  const { data: adminCount } = useUserCountQuery({ role: 'SiteAdmin' });
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();
  const { setTitle } = useMainLayout();
  const { user } = useAuthStore();
  const { data: objectclassList } = useAllObjectClassListQuery();
  const { data: tenantUsageInfo } = useTenantUsageQuery();
  const { getVariant, isEnabled } = useUnleash();

  const extendClassManagable = getVariant('vuex.sapporo.extendClassManagable');
  const isExtendClassManagable = extendClassManagable?.feature_enabled;
  const isBIPAVerifiedTenant = isEnabled('vuex.sapporo.bipa');

  /**
   * AI Annotation 1frame = 1$ / Storage 1GB = $10.24 / AI Training 1hour = $120
   */
  const totalPricing =
    !isBIPAVerifiedTenant || !tenantUsageInfo
      ? ''
      : +(
          tenantUsageInfo.usage.annotationCount * 1 +
          (tenantUsageInfo.usage.s3CurrentBytes / 1024 / 1024 / 1024) * 10.24 +
          (tenantUsageInfo.usage.trainingTime / 60) * 120
        ).toFixed(2);

  useEffect(() => {
    setTitle('Admin settings');
  }, [currentTenant]);

  return (
    <>
      <Container>
        <ContainerHeader>
          <div className="w-fit pt-6 pb-2">
            {user.role === 'Admin' ||
              (user.role === 'SiteAdmin' && (
                <VTab
                  defaultValue="1"
                  onValueChange={val => {
                    if (val === '0') navigate('/member');
                  }}
                >
                  <VTabTriggerContainer>
                    <VTabTrigger value="0"> Members </VTabTrigger>
                    <VTabTrigger value="1"> Admin settings </VTabTrigger>
                  </VTabTriggerContainer>
                </VTab>
              ))}
          </div>
        </ContainerHeader>

        <ContainerContent>
          <VFlex className="w-full justify-center p-6">
            <VStack className="gap-14">
              <VStack className="gap-10 text-primary-500">
                <VStack gap={1}>
                  <VFlex gap={0.25} items="center">
                    <CheckCircle className="h-6 w-6 text-gray-500" />
                    <div className="text-body-xl text-gray-500">My Plan</div>
                    {totalPricing && (
                      <>
                        <div className="mx-1 h-4 w-px bg-gray-400"></div>
                        <div className="text-body-xl font-semibold text-primary-400">
                          ${totalPricing}
                        </div>
                      </>
                    )}
                  </VFlex>

                  <VFlex gap={1}>
                    {currentTenant && <Admin.PlanCard tenant={currentTenant} />}

                    <VStack className="w-[664px] gap-4">
                      <VFlex className="w-full gap-4">
                        <VStack
                          gap={0.25}
                          className="h-[88px] flex-1 rounded-md bg-Primary-Navy-Active p-4 "
                        >
                          <VFlex gap={0.25}>
                            <Cloud className="h-5 w-5" />
                            <div className="text-body-lg">
                              Used
                              {isBIPAVerifiedTenant ? '' : ' / Total storage'}
                            </div>
                          </VFlex>
                          <VFlex
                            justify="end"
                            items="end"
                            className="w-full flex-1"
                            gap={0.25}
                          >
                            <div className="text-headline-sm font-semibold">
                              {bytesToGB(
                                Number(
                                  tenantUsageInfo?.usage?.s3CurrentBytes ?? 0,
                                ),
                              ).toFixed(2)}{' '}
                              GB
                            </div>
                            {!isBIPAVerifiedTenant && (
                              <div className="text-body-xl">
                                /
                                {Math.floor(
                                  bytesToGB(
                                    Number(
                                      tenantUsageInfo?.usageLimit
                                        ?.s3UsageLimit ?? 0,
                                    ),
                                  ),
                                )}{' '}
                                GB
                              </div>
                            )}
                          </VFlex>
                        </VStack>

                        <VStack
                          gap={0.25}
                          className="h-[88px] flex-1 rounded-md bg-Primary-Navy-Active p-4 "
                        >
                          <VFlex gap={0.25}>
                            <SmartToy className="h-5 w-5" />
                            <div className="text-body-lg text-nowrap">
                              Used
                              {isBIPAVerifiedTenant
                                ? ''
                                : ' / Total AI-annotation'}
                            </div>
                          </VFlex>
                          <VFlex
                            justify="end"
                            items="end"
                            className="w-full flex-1"
                            gap={0.25}
                          >
                            <div className="text-headline-sm font-semibold">
                              {Number(
                                tenantUsageInfo?.usage?.annotationCount ?? 0,
                              )}
                            </div>
                            {!isBIPAVerifiedTenant && (
                              <div className="text-body-xl">
                                /{' '}
                                {Number(
                                  tenantUsageInfo?.usageLimit
                                    ?.annotationUsageLimit ?? 0,
                                )}
                              </div>
                            )}
                          </VFlex>
                        </VStack>

                        <VStack
                          gap={0.25}
                          className="h-[88px] flex-1 rounded-md bg-Primary-Navy-Active p-4 "
                        >
                          <VFlex gap={0.25}>
                            <Schedule className="h-5 w-5" />
                            <div className="text-body-lg text-nowrap">
                              Used
                              {isBIPAVerifiedTenant
                                ? ''
                                : ' / Total training time'}
                            </div>
                          </VFlex>
                          <VFlex
                            justify="end"
                            items="end"
                            className="w-full flex-1"
                            gap={0.25}
                          >
                            <div className="text-headline-sm font-semibold">
                              {minutesToHM(
                                tenantUsageInfo?.usage?.trainingTime ?? 0,
                              )}
                            </div>
                            {!isBIPAVerifiedTenant && (
                              <div className="text-body-xl">
                                /{' '}
                                {minutesToH(
                                  tenantUsageInfo?.usageLimit
                                    ?.trainingTimeLimit ?? 0,
                                )}
                              </div>
                            )}
                          </VFlex>
                        </VStack>
                      </VFlex>

                      <VFlex gap={1}>
                        <VFlex
                          justify="between"
                          items="center"
                          className="h-[56px] flex-1 rounded-md bg-Primary-Navy-Active p-4"
                        >
                          <VFlex gap={0.25}>
                            <Groups className="h-5 w-5" />
                            <div className="text-body-lg">
                              Total Seats{' '}
                              <span className="text-body-xl font-bold">
                                {tenantUsageInfo?.usageLimit.userLimit ?? 0}
                              </span>
                            </div>
                          </VFlex>
                          <VFlex
                            justify="end"
                            items="end"
                            className="w-full flex-1"
                          >
                            <VFlex items="center" gap={0.5}>
                              <VFlex gap={0.5} items="center">
                                <VFlex className="gap-[2px]" items="center">
                                  <div className="text-body-md">Admin</div>
                                </VFlex>
                                <div className="text-body-lg font-semibold">
                                  {adminCount ?? 0}
                                </div>
                              </VFlex>

                              <div className="h-4 w-px bg-gray-300" />

                              <VFlex gap={0.5} items="center">
                                <VFlex className="gap-[2px]" items="center">
                                  <div className="text-body-md">Manager</div>
                                </VFlex>
                                <div className="text-body-lg font-semibold">
                                  {managerCount ?? 0}
                                </div>
                              </VFlex>

                              <div className="h-4 w-px bg-gray-300" />

                              <VFlex gap={0.5} items="center">
                                <VFlex className="gap-[2px]" items="center">
                                  <div className="text-body-md">Labeler</div>
                                </VFlex>
                                <div className="text-body-lg font-semibold">
                                  {workerCount ?? 0}
                                </div>
                              </VFlex>
                            </VFlex>
                          </VFlex>
                        </VFlex>

                        <VFlex
                          justify="between"
                          items="center"
                          className="h-[56px] w-[212px] rounded-md bg-Primary-Navy-Active p-4 text-primary-500"
                        >
                          <VFlex
                            items="center"
                            justify="between"
                            className="w-full"
                            gap={0.5}
                          >
                            <VFlex
                              items="center"
                              justify="between"
                              className="flex-1"
                            >
                              <VFlex gap={0.25} items="center">
                                <DirectionsCar className="size-5" />
                                <span className="text-body-lg">
                                  Total classes
                                </span>
                              </VFlex>

                              <span className="text-body-xl font-bold">
                                {objectclassList?.length ?? 0}
                              </span>
                            </VFlex>

                            {isExtendClassManagable && (
                              <OpenObjectClassManagementButton />
                            )}
                          </VFlex>
                        </VFlex>
                      </VFlex>
                    </VStack>
                  </VFlex>
                </VStack>
              </VStack>

              <VFlex justify="between" className="px-4" items="center">
                <ChangePlan.Button />
                {/* <VTextButton
                  className="font-bold text-gray-500"
                  onClick={onChangePlanClick}
                >
                  Change plan
                </VTextButton> */}
                <VTextButton
                  className="font-bold text-error-500"
                  onClick={() => setVisible(true)}
                >
                  Delete workspace
                </VTextButton>
              </VFlex>
            </VStack>
          </VFlex>
        </ContainerContent>
      </Container>

      <VDialog visible={visible} onUpdateVisible={setVisible}>
        <div className="h-[657px] w-[1020px] rounded-lg bg-white p-6">
          <Admin.RemoveWorkSpace close={() => setVisible(false)} />
        </div>
      </VDialog>
    </>
  );
};

export default AdminSettingPage;
